/* DreamWeaver — the Soul of the Customer digital form.
 * Public, iframe-embeddable, no login. 10 steps · 85 questions.
 * Modes: /dreamweaver?client=me (open, self-identified)
 *        /dreamweaver?token=… (personalised member link, prefilled)
 * Only Step 1's name/email/mobile are required — members may submit
 * from any step after that. Luxury editorial styling per client theme. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputBase = {
  width: '100%', padding: '12px 14px', fontSize: 15,
  border: '1px solid #D8D2C4', borderRadius: 10, background: '#FFFFFF',
  color: '#1B2A4A', outline: 'none', fontFamily: 'inherit',
  transition: 'border-color .15s',
};

function Field({ q, value, onChange, accent }) {
  const set = (v) => onChange(q.key, v);
  const label = (
    <label style={{ display: 'block', fontSize: 13.5, fontWeight: 600,
                     color: '#3B3528', marginBottom: 6 }}>
      {q.label}
      {q.required && <span style={{ color: accent }}> *</span>}
      {q.hint && (
        <span style={{ fontWeight: 400, color: '#9A927F', marginLeft: 8,
                        fontSize: 12 }}>{q.hint}</span>
      )}
    </label>
  );
  if (q.type === 'long') {
    return (
      <div style={{ marginBottom: 22 }}>{label}
        <textarea data-testid={`dw-field-${q.key}`} rows={3} value={value || ''}
          onChange={(e) => set(e.target.value)} style={{ ...inputBase, resize: 'vertical' }} />
      </div>
    );
  }
  if (q.type === 'select') {
    return (
      <div style={{ marginBottom: 22 }}>{label}
        <select data-testid={`dw-field-${q.key}`} value={value || ''}
          onChange={(e) => set(e.target.value)} style={inputBase}>
          <option value="">—</option>
          {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (q.type === 'multiselect') {
    const sel = Array.isArray(value) ? value : [];
    const toggle = (o) => set(sel.includes(o) ? sel.filter((x) => x !== o) : [...sel, o]);
    return (
      <div style={{ marginBottom: 22 }}>{label}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} data-testid={`dw-field-${q.key}`}>
          {q.options.map((o) => {
            const on = sel.includes(o);
            return (
              <button key={o} type="button" onClick={() => toggle(o)}
                data-testid={`dw-chip-${q.key}-${o.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                style={{
                  padding: '8px 14px', borderRadius: 999, fontSize: 13.5,
                  cursor: 'pointer', fontFamily: 'inherit',
                  border: `1.5px solid ${on ? accent : '#D8D2C4'}`,
                  background: on ? accent : '#FFFFFF',
                  color: on ? '#FFFFFF' : '#3B3528',
                  transition: 'background-color .15s, border-color .15s',
                }}>{o}</button>
            );
          })}
        </div>
      </div>
    );
  }
  const type = q.type === 'date' ? 'text' : q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : 'text';
  return (
    <div style={{ marginBottom: 22 }}>{label}
      <input data-testid={`dw-field-${q.key}`} type={type} value={value || ''}
        placeholder={q.type === 'date' ? 'DD-MM-YYYY' : ''}
        onChange={(e) => set(e.target.value)} style={inputBase} />
    </div>
  );
}

export default function DreamWeaverForm() {
  const [params] = useSearchParams();
  const client = params.get('client') || 'me';
  const token = params.get('token') || '';
  const [schema, setSchema] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/dreamweaver/schema?client=${client}&token=${token}`)
      .then((r) => {
        setSchema(r.data);
        if (r.data.prefill) setAnswers((a) => ({ ...r.data.prefill, ...a }));
      })
      .catch(() => setError('Could not load the form. Please refresh.'));
  }, [client, token]);

  const onChange = useCallback((k, v) => {
    setAnswers((a) => ({ ...a, [k]: v }));
  }, []);

  const theme = schema?.theme || { accent: '#1B2A4A', accent_dark: '#0E1626', brand: 'Concierge®' };
  const steps = schema?.steps || [];
  const current = steps[step];
  const step1Ok = useMemo(() =>
    (answers.full_name || '').trim() && (answers.email || '').trim() && (answers.mobile || '').trim(),
    [answers.full_name, answers.email, answers.mobile]);

  const submit = async () => {
    if (!step1Ok) { setError('Please fill your name, email and mobile in Step 1.'); setStep(0); return; }
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/dreamweaver/submit`, {
        client_id: schema.client_id, token, answers,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong — please try again.');
    } finally { setSubmitting(false); }
  };

  if (error && !schema) {
    return <div style={{ padding: 60, textAlign: 'center', fontFamily: 'Georgia, serif' }}>{error}</div>;
  }
  if (!schema) {
    return <div style={{ padding: 60, textAlign: 'center', color: '#9A927F', fontFamily: 'Georgia, serif' }}>Preparing your DreamWeaver…</div>;
  }

  if (done) {
    return (
      <div style={{ minHeight: '100vh', background: '#FBF7EF', display: 'flex',
                     alignItems: 'center', justifyContent: 'center', padding: 24 }}
           data-testid="dw-thankyou">
        <div style={{ maxWidth: 520, textAlign: 'center' }}>
          <div style={{ fontSize: 46, marginBottom: 16, color: theme.accent,
                         fontFamily: 'Fraunces, Georgia, serif' }}>✦</div>
          <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 30,
                        color: theme.accent_dark, marginBottom: 12 }}>
            Thank you, {(answers.preferred_name || answers.full_name || '').split(' ')[0]}.
          </h1>
          <p style={{ color: '#6B6355', fontSize: 15.5, lineHeight: 1.7 }}>
            Your DreamWeaver is with your Concierge® team now. Every answer
            you shared becomes a thread in how we craft your experiences —
            quietly, personally, perfectly.
          </p>
          <p style={{ color: '#9A927F', fontSize: 13, marginTop: 22 }}>{theme.brand}</p>
        </div>
      </div>
    );
  }

  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div style={{ minHeight: '100vh', background: '#FBF7EF',
                   fontFamily: "'Inter', -apple-system, sans-serif" }}
         data-testid="dreamweaver-form">
      {/* header */}
      <div style={{ background: theme.accent_dark, padding: '26px 24px 22px', textAlign: 'center' }}>
        <div style={{ color: '#E9DFC9', fontSize: 12, letterSpacing: 3,
                       textTransform: 'uppercase', marginBottom: 6 }}>{theme.brand}</div>
        <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', color: '#FFFFFF',
                      fontSize: 27, margin: 0 }}>The DreamWeaver</h1>
        <div style={{ color: '#B9AF9A', fontSize: 13.5, marginTop: 6 }}>
          Not just your details — the soul behind them.
        </div>
      </div>
      {/* progress */}
      <div style={{ height: 4, background: '#E9E2D2' }}>
        <div style={{ height: 4, width: `${progress}%`, background: theme.accent,
                       transition: 'width .35s ease' }} data-testid="dw-progress" />
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '30px 22px 90px' }}>
        <div style={{ fontSize: 12.5, color: theme.accent, fontWeight: 700,
                       letterSpacing: 1.5, textTransform: 'uppercase' }}
             data-testid="dw-step-indicator">
          Step {step + 1} of {steps.length}
        </div>
        <h2 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 24,
                      color: '#1B2A4A', margin: '6px 0 2px' }} data-testid="dw-step-title">
          {current.title}
        </h2>
        <p style={{ color: '#9A927F', fontSize: 14, marginBottom: 26 }}>{current.subtitle}</p>

        {current.questions.map((qq) => (
          <Field key={qq.key} q={qq} value={answers[qq.key]}
                 onChange={onChange} accent={theme.accent} />
        ))}

        {error && (
          <div style={{ color: '#B91C1C', fontSize: 14, marginBottom: 14 }}
               data-testid="dw-error">{error}</div>
        )}

        {/* nav */}
        <div style={{ display: 'flex', gap: 12, marginTop: 10, alignItems: 'center' }}>
          {step > 0 && (
            <button type="button" onClick={() => { setStep(step - 1); window.scrollTo(0, 0); }}
              data-testid="dw-back-btn"
              style={{ padding: '13px 26px', borderRadius: 999, fontSize: 15,
                        border: '1.5px solid #C9C1AE', background: 'transparent',
                        color: '#6B6355', cursor: 'pointer', fontFamily: 'inherit' }}>
              ← Back
            </button>
          )}
          {step < steps.length - 1 && (
            <button type="button"
              onClick={() => {
                if (step === 0 && !step1Ok) { setError('Please fill your name, email and mobile to continue.'); return; }
                setError(''); setStep(step + 1); window.scrollTo(0, 0);
              }}
              data-testid="dw-next-btn"
              style={{ padding: '13px 34px', borderRadius: 999, fontSize: 15,
                        fontWeight: 600, border: 'none', background: theme.accent,
                        color: '#FFFFFF', cursor: 'pointer', fontFamily: 'inherit' }}>
              Continue →
            </button>
          )}
          {step > 0 && (
            <button type="button" onClick={submit} disabled={submitting}
              data-testid="dw-submit-btn"
              style={{ padding: '13px 34px', borderRadius: 999, fontSize: 15,
                        fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        border: step === steps.length - 1 ? 'none' : `1.5px solid ${theme.accent}`,
                        background: step === steps.length - 1 ? theme.accent : 'transparent',
                        color: step === steps.length - 1 ? '#FFFFFF' : theme.accent,
                        opacity: submitting ? 0.6 : 1, marginLeft: 'auto' }}>
              {submitting ? 'Sending…' : step === steps.length - 1 ? '✦ Submit my DreamWeaver' : 'Submit now'}
            </button>
          )}
        </div>
        <p style={{ color: '#B9AF9A', fontSize: 12, marginTop: 18 }}>
          Answer only what you'd like — every detail helps us serve you better.
          Your information stays private with your Concierge® team.
        </p>
      </div>
    </div>
  );
}
